import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { enqueueSnackbar } from 'notistack';
import FileAPI from 'api/FileAPI';

interface IBulkUploadProductModalProps {
  open: boolean;
  handleClose: () => void;
  title?: string;
  database: any;
  product?: any;
  handleRefresh?: any;
}

export const AddUploadModal: React.FC<IBulkUploadProductModalProps> = ({
  open,
  handleClose,
  title = 'Upload Document',
  database,
  product,
  handleRefresh,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleFileUpload = async () => {
    if (!file || isLoading) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await FileAPI.uploadFile(
        file,
        database?.id,
        product?.database_product_id,
      );
      if (response?.success) {
        enqueueSnackbar(`File ${file?.name} was successfulyy uploaded!`, {
          variant: 'success',
        });
        setFile(null);
        setIsLoading(false);
        if (handleRefresh) {
          handleRefresh();
        }
        handleClose();
      }
    } catch (e) {
      console.error(`Error while uploading document: ${e}`);
      enqueueSnackbar(`Error while uploading document: ${e}`, {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (isLoading) {
      return;
    }
    const accptFile = acceptedFiles[0];
    if (!accptFile) {
      return;
    }

    // Convert to standard File
    const fileLocal = new File([accptFile], accptFile.name, {
      type: accptFile.type,
    });
    setFile(fileLocal);

    try {
      await fileHandler(fileLocal);
      enqueueSnackbar('File ready for submission!', { variant: 'success' });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error processing file. Please ensure it is a valid CSV or XLSX.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setFile(null);
    }
  };

  const fileHandler = async (fileLocal: any) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (
          fileLocal.type === 'text/csv' ||
          fileLocal.name.endsWith('.csv') ||
          fileLocal.name.endsWith('.xlsx') ||
          fileLocal.name.endsWith('.docx') ||
          fileLocal.name.endsWith('.pdf') ||
          fileLocal.name.endsWith('.jpeg') ||
          fileLocal.name.endsWith('.jpg') ||
          fileLocal.name.endsWith('.png')
        ) {
          resolve({
            name: fileLocal.name,
            size: fileLocal.size,
            type: fileLocal.type,
          });
        } else {
          console.error(
            'Mock fileHandler error: Unsupported file type:',
            fileLocal.type,
            fileLocal.name,
          );
          reject(
            new Error(
              'Mock fileHandler: Unsupported file type. Please upload CSV or XLSX.',
            ),
          );
        }
      }, 500);
    });
  };

  const acceptedExtensions = [
    '.csv',
    '.xlsx',
    '.png',
    '.jpeg',
    '.jpg',
    '.pdf',
    '.docx',
  ];

  const fileValidator = (currentFile: File) => {
    if (currentFile.size > 10 * 1024 * 1024) {
      enqueueSnackbar('Your file is too large, upload a file less than 10MB', {
        variant: 'error',
      });
      return { code: 'size-too-large', message: 'File is larger than 10MB' };
    }
    const acceptedMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'image/png',
      'image/jpeg',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    const fileExtension = `.${currentFile.name
      .split('.')
      .pop()
      ?.toLowerCase()}`;

    if (
      !acceptedMimeTypes.includes(currentFile.type) &&
      !acceptedExtensions.includes(fileExtension)
    ) {
      enqueueSnackbar(
        'Invalid file type. Please upload a CSV, Excel (XLSX), PNG, JPEG, JPG, PDF, or Word (DOCX) file.',
        {
          variant: 'error',
        },
      );
      return {
        code: 'file-type-invalid',
        message:
          'File type must be one of: .csv, .xlsx, .png, .jpeg, .jpg, .pdf, .docx',
      };
    }
    return null;
  };

  const handleDeleteFile = () => {
    setFile(null);
    enqueueSnackbar('File removed.', { variant: 'info' });
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    accept: acceptedExtensions,
    multiple: false,
  });

  const dropzoneClassName = useMemo(() => {
    const baseClass = 'dropzone-base';
    if (isDragAccept) {
      return `${baseClass} dropzone-accept`;
    }
    if (isDragReject) {
      return `${baseClass} dropzone-reject`;
    }
    if (isDragActive) {
      return `${baseClass} dropzone-active`;
    }
    return file
      ? `${baseClass} dropzone-file-present`
      : `${baseClass} dropzone-default`;
  }, [isDragActive, isDragAccept, isDragReject, file]);

  return (
    <>
      <Dialog
        className='dialog_box'
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            overflowY: 'visible',
            maxWidth: '800px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle
          id='bulk-upload-product-modal-title'
          className='modal-header-content modal-title'
        >
          {title}
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={handleClose}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <div className='audit-add-modal-cont-body-frame'>
              <div className='audit-add-modal-cont-body-frame-form'>
                <div className='audit-add-modal-head-cont-supporting'>
                  Upload document for the database {database.name}{' '}
                  {product && ` and product ${product.name}`}
                </div>
                <div className='audit-add-modal-head-cont-supporting'>
                  <div {...getRootProps()} className={dropzoneClassName}>
                    <input {...getInputProps()} />
                    {file ? (
                      <div className='dropzone-content-item'>
                        <svg
                          className='dropzone-icon-success'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          strokeWidth={1.5}
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                          />
                        </svg>
                        <p className='dropzone-filename'>{file.name}</p>
                        <p className='dropzone-filesize'>
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFile();
                          }}
                          className='dropzone-remove-button'
                        >
                          Remove File
                        </button>
                      </div>
                    ) : isLoading ? (
                      <div className='dropzone-content-item'>
                        Processing file...
                      </div>
                    ) : (
                      <div className='dropzone-content-item'>
                        <p className='dropzone-text'>
                          <span className='dropzone-text-highlight'>
                            Click to upload
                          </span>{' '}
                          or drag and drop
                        </p>
                        <p className='dropzone-subtext'>
                          CSV, XLSX, DOCX, PDF, JPEG, JPG, PNG (max 10MB)
                        </p>
                        {isDragReject && (
                          <p className='dropzone-error-text'>
                            Invalid file type or size.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Upload Button */}
              <div className='audit-modal-cont-footer'>
                <div className='audit-modal-cont-footer-content'>
                  <div
                    className='audit-modal-cont-footer-content-btn-cancel'
                    onClick={handleClose}
                  >
                    <div className='audit-modal-cont-footer-content-btn-text'>
                      Cancel
                    </div>
                  </div>
                  <div className='audit-modal-cont-footer-content-btn-upload'>
                    <div
                      className={`audit-modal-cont-footer-content-btn-text ${
                        isLoading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      onClick={!isLoading ? handleFileUpload : undefined}
                      style={{ color: '#FFF' }}
                    >
                      {isLoading ? 'Uploading...' : 'Upload'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
